/* Quotes the complete PC and selected accessories through the existing read-only validator. */
(() => {
  'use strict';
  const formatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
  const versions = new WeakMap();
  const filters = new WeakMap();
  const initialLabels = new WeakMap();
  const originalLinks = new WeakMap();
  async function quote(card) {
    const version = (versions.get(card) || 0) + 1;
    versions.set(card, version);
    const price = card.querySelector('[data-bundle-price]');
    const status = card.querySelector('[data-bundle-status]');
    const link = card.querySelector('[data-bundle-link]');
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    price.classList.add('is-pending');
    price.textContent = 'Checking bundle price…';
    try {
      const selection = JSON.parse(card.querySelector('[data-bundle-selection]').textContent);
      const detail = filters.get(card);
      for (const entry of detail?.selections || []) {
        const key = String(entry.param || '').replace(/^elite_/, '');
        if (!['cpu','ram','gpu','psu','nvme_ssd','sata_ssd','sata_hdd','cooler','wifi','windows_key','kettle_lead','mobo'].includes(key)) continue;
        if (['__no_hdd__','__no_sata_ssd__'].includes(entry.value)) delete selection.selections[key];
        else if (entry.value) selection.selections[key] = entry.value;
      }
      const url = new URL(originalLinks.get(card), location.origin);
      for (const key of ['case','cpu','mobo','ram','gpu','psu','cooler','nvme_ssd','sata_ssd','sata_hdd','wifi','windows_key','kettle_lead']) url.searchParams.delete('elite_' + key);
      Object.entries(selection.selections).forEach(([key,value]) => url.searchParams.set('elite_' + key,value));
      link.href = url.pathname + url.search;
      link.textContent = 'Configure bundle';
      card.querySelectorAll('[data-bundle-spec]').forEach(node => {
        const key = node.dataset.bundleSpec;
        const changed = detail?.selections?.some(entry => entry.param === 'elite_' + key);
        node.textContent = changed ? detail.label(key,selection.selections[key]) || initialLabels.get(card)[key] : initialLabels.get(card)[key];
      });
      const response = await (window.ElitePCQuoteTransport?.fetch || fetch)('/apps/pc-configurator?action=validate', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(selection), signal: controller.signal,
      });
      const result = await response.json();
      if (versions.get(card) !== version) return;
      if (!response.ok || !result.success) throw new Error('Quote unavailable');
      if (!result.compatible) {
        price.textContent = 'Review your choices';
        status.textContent = 'Some parts need changing. View compatible alternatives.';
        link.textContent = 'Review bundle';
        return;
      }
      const quoted = window.ElitePCPriceAddition?.quote(result.price) || result.price;
      if (quoted?.currency !== 'GBP' || !Number.isFinite(quoted.amount) || quoted.amount <= 0) throw new Error('Invalid price');
      price.textContent = formatter.format(quoted.amount);
      price.classList.remove('is-pending');
      status.textContent = 'PC + mouse + headset included';
    } catch {
      if (versions.get(card) !== version) return;
      price.textContent = 'See current price';
      status.textContent = 'Review price and availability in the configurator.';
    } finally {
      window.clearTimeout(timeout);
      if (versions.get(card) === version) price.classList.remove('is-pending');
    }
  }
  function init(root) {
    if (root.dataset.bundlesReady) return;
    root.dataset.bundlesReady = 'true';
    const cards = [...root.querySelectorAll('[data-bundle-card]')];
    cards.forEach(card => {
      initialLabels.set(card,Object.fromEntries([...card.querySelectorAll('[data-bundle-spec]')].map(node => [node.dataset.bundleSpec,node.textContent])));
      originalLinks.set(card,card.querySelector('[data-bundle-link]').getAttribute('href'));
    });
    document.addEventListener('elite:page-builder:filter', event => {
      const detail = event.detail;
      if (detail.page !== root) return;
      if (!detail.active && !root.dataset.bundleFiltersApplied) return;
      root.dataset.bundleFiltersApplied = String(detail.active);
      let visible = 0;
      for (const card of cards) {
        const sku = JSON.parse(card.querySelector('[data-bundle-selection]').textContent).selections.case;
        const match = !detail.active || detail.caseMatches({sku,available:card.dataset.bundleAvailable === 'true'});
        card.hidden = !match;
        card.style.display = match ? '' : 'none';
        filters.set(card,detail);
        card.querySelector('.epc-bundles__description').hidden = detail.active;
        if (match) { visible++; quote(card); }
        else versions.set(card,(versions.get(card) || 0) + 1);
      }
      const status = root.querySelector('[data-bundle-filter-status]');
      status.hidden = !detail.active;
      status.textContent = visible ? `${visible} matching bundle${visible === 1 ? '' : 's'}` : 'No bundles match these filters. Change a selection or clear the filters.';
    });
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        for (const entry of entries) if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          quote(entry.target);
        }
      }, { rootMargin: '300px' });
      cards.forEach(card => observer.observe(card));
    } else cards.forEach(quote);
  }
  document.querySelectorAll('[data-elite-bundles]').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-elite-bundles]').forEach(init));
})();
